import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { LoadingState, EmptyState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { useAuth } from '@/hooks/useAuth';
import { useVendors } from '../useVendors';
import { useCreateVendor } from '../useCreateVendor';
import type { NewVendorInput } from '../types';

const EMPTY_FORM: NewVendorInput = { name: '', gstin: '', contact_person: '', phone: '', email: '', address: '' };

export default function VendorsPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<NewVendorInput>(EMPTY_FORM);
  const [validationError, setValidationError] = useState('');

  const vendorsQuery = useVendors();
  const createVendor = useCreateVendor();

  const f = <K extends keyof NewVendorInput>(k: K, v: NewVendorInput[K]) => setForm((p) => ({ ...p, [k]: v }));

  const submit = () => {
    if (!form.name.trim()) { setValidationError('Vendor name is required'); return; }
    setValidationError('');
    createVendor.mutate(form, {
      onSuccess: () => { setForm(EMPTY_FORM); setAdding(false); },
    });
  };

  const error = validationError || (createVendor.error instanceof ApiError ? createVendor.error.message : null);
  const vendors = vendorsQuery.data;

  if (vendorsQuery.isLoading || vendors === undefined) return <LoadingState />;

  return (
    <Card>
      <div className="mb-3.5 flex items-center justify-between">
        <div className="text-sm font-semibold">Vendors</div>
        {/* Hidden once the list is empty — the EmptyState below carries the
            same "+ New vendor" action there, and showing it in both places
            at once is the exact redundancy backlog #20's action-bearing
            EmptyState is meant to replace, not add to. */}
        {isAdmin && !adding && vendors && vendors.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>+ New vendor</Button>
        )}
      </div>
      {error && <div className="mb-3 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}
      {adding && (
        <div className="mb-3.5 rounded-[var(--radius)] bg-canvas p-3">
          <div className="grid2 mb-2.5">
            <FormField label="Vendor name" required><Input value={form.name} onChange={(e) => f('name', e.target.value)} /></FormField>
            <FormField label="GSTIN"><Input value={form.gstin} onChange={(e) => f('gstin', e.target.value)} /></FormField>
            <FormField label="Contact person"><Input value={form.contact_person} onChange={(e) => f('contact_person', e.target.value)} /></FormField>
            <FormField label="Phone"><Input value={form.phone} onChange={(e) => f('phone', e.target.value)} /></FormField>
          </div>
          <div className="flex gap-2">
            <Button size="sm" disabled={createVendor.isPending} loading={createVendor.isPending} onClick={submit}>Save</Button>
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-2">
        {vendors.map((v) => (
          <div key={v.id} className="rounded-[var(--radius)] border border-line p-2.5">
            <div className="text-[13px] font-semibold">{v.name}</div>
            <div className="text-[11.5px] text-ink-muted">
              {v.gstin ? `GSTIN: ${v.gstin} · ` : ''}{v.contact_person} {v.phone && `· 📱 ${v.phone}`}
            </div>
          </div>
        ))}
        {vendors.length === 0 && !adding && (
          <EmptyState
            label="No vendors yet."
            action={isAdmin ? { label: '+ New vendor', onClick: () => setAdding(true) } : undefined}
          />
        )}
      </div>
    </Card>
  );
}
